
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Trash2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface ResumeSelectionProps {
  originalResumes: any[];
  selectedResumeId: string | null;
  onResumeSelect: (resumeId: string) => void;
  onDelete: (resumeId: string) => Promise<void>;
  refetchOriginalResumes: () => void;
}

const ResumeSelection = ({
  originalResumes,
  selectedResumeId,
  onResumeSelect,
  onDelete,
  refetchOriginalResumes,
}: ResumeSelectionProps) => {
  if (!originalResumes.length) {
    return null;
  }

  return (
    <div className="space-y-2">
      <Label htmlFor="resume-select">Select an existing resume</Label>
      <div className="flex gap-2 items-center">
        <div className="flex-1">
          <Select
            value={selectedResumeId || "new-upload"}
            onValueChange={onResumeSelect}
          >
            <SelectTrigger>
              <SelectValue placeholder="Choose a resume" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="new-upload">Upload a new resume</SelectItem>
              {originalResumes.map((resume) => (
                <SelectItem key={resume.id} value={resume.id} className="flex justify-between group">
                  <span className="truncate">
                    {resume.original_filename || resume.title}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        {selectedResumeId && selectedResumeId !== "new-upload" && (
          <Button 
            variant="destructive" 
            size="icon" 
            onClick={() => {
              const resumeToDelete = originalResumes.find(resume => resume.id === selectedResumeId);
              if (resumeToDelete) {
                onDelete(resumeToDelete.id);
              }
            }}
            title="Delete selected resume"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
      
      {originalResumes.length > 0 && (
        <div className="mt-2 text-xs text-gray-500 flex justify-between items-center">
          <span>{originalResumes.length} resume{originalResumes.length !== 1 ? 's' : ''} available</span>
          <Button 
            variant="link" 
            className="h-auto p-0 text-xs" 
            onClick={refetchOriginalResumes}
          >
            Refresh list
          </Button>
        </div>
      )}
    </div>
  );
};

export default ResumeSelection;
